import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiErrorMessage } from '../../shared/api/errors';
import { useDebouncedValue } from '../../shared/lib/useDebouncedValue';
import { Button } from '../../shared/ui/Button';
import { inputClass } from '../../shared/ui/TextField';
import { Card, EmptyState, ErrorText, PageHeader, Spinner } from '../../shared/ui/primitives';
import {
  createClinic,
  deleteClinic,
  listClinics,
  updateClinic,
  type Clinic,
  type ClinicInput,
} from '../clinics/api';

export function ClinicsPage() {
  const queryClient = useQueryClient();
  const [searchInput, setSearchInput] = useState('');
  const search = useDebouncedValue(searchInput.trim(), 300);
  const [editing, setEditing] = useState<Clinic | null>(null);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['admin', 'clinics', search],
    queryFn: () => listClinics({ search: search || undefined, limit: 200 }),
  });

  const { register, handleSubmit, reset } = useForm<ClinicInput>();

  const invalidate = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['admin', 'clinics'] }),
      queryClient.invalidateQueries({ queryKey: ['clinics'] }),
      queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] }),
    ]);

  const save = useMutation({
    mutationFn: (body: ClinicInput) => (editing ? updateClinic(editing.id, body) : createClinic(body)),
    onSuccess: async () => {
      await invalidate();
      reset({ name: '', city: '', address: '', phone: '' });
      setEditing(null);
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => deleteClinic(id),
    onSuccess: invalidate,
  });

  const startEdit = (c: Clinic) => {
    setEditing(c);
    reset({ name: c.name, city: c.city, address: c.address, phone: c.phone });
  };
  const cancelEdit = () => {
    setEditing(null);
    reset({ name: '', city: '', address: '', phone: '' });
  };

  return (
    <section className="space-y-5">
      <PageHeader title="Клиники" />

      <Card className="p-4 sm:p-5">
        <form
          onSubmit={handleSubmit((v) => {
            if (v.name?.trim()) save.mutate(v);
          })}
          className="grid gap-3 sm:grid-cols-2"
        >
          <p className="text-sm font-semibold text-ink sm:col-span-2">
            {editing ? `Изменение: ${editing.name}` : 'Новая клиника'}
          </p>
          <input className={inputClass} placeholder="Название *" {...register('name')} />
          <input className={inputClass} placeholder="Город" {...register('city')} />
          <input className={inputClass} placeholder="Адрес" {...register('address')} />
          <input className={inputClass} placeholder="Телефон" {...register('phone')} />
          <div className="flex flex-wrap items-center gap-2 sm:col-span-2">
            <Button type="submit" disabled={save.isPending}>
              {editing ? 'Сохранить' : 'Добавить'}
            </Button>
            {editing && (
              <Button type="button" variant="secondary" onClick={cancelEdit}>
                Отмена
              </Button>
            )}
            {save.isError && (
              <span className="text-sm text-danger">{apiErrorMessage(save.error, 'Не удалось сохранить')}</span>
            )}
          </div>
        </form>
      </Card>

      <input
        type="search"
        value={searchInput}
        onChange={(e) => setSearchInput(e.target.value)}
        placeholder="Поиск по названию или городу"
        className={`${inputClass} sm:max-w-xs`}
      />

      {isLoading && <Spinner />}
      {isError && <ErrorText>{apiErrorMessage(error)}</ErrorText>}
      {data && data.items.length === 0 && <EmptyState>Клиник пока нет.</EmptyState>}

      {data && data.items.length > 0 && (
        <Card className="divide-y divide-line overflow-hidden">
          {data.items.map((c) => (
            <div
              key={c.id}
              className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="min-w-0">
                <p className="font-medium text-ink">{c.name}</p>
                <p className="text-sm text-muted">
                  {[c.city, c.address, c.phone].filter(Boolean).join(' · ') || '—'}
                </p>
              </div>
              <div className="flex shrink-0 gap-3 text-xs">
                <button
                  type="button"
                  onClick={() => startEdit(c)}
                  className="font-medium text-accent hover:text-accent-600"
                >
                  изменить
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (window.confirm(`Удалить клинику «${c.name}»?`)) remove.mutate(c.id);
                  }}
                  className="text-muted hover:text-danger"
                >
                  удалить
                </button>
              </div>
            </div>
          ))}
        </Card>
      )}
      {remove.isError && <ErrorText>{apiErrorMessage(remove.error, 'Не удалось удалить')}</ErrorText>}
    </section>
  );
}
